import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { SectionIndices } from '../../../common/i-doc/i-doc.registry.ts';
import { clientMetricsCards as c } from '@/styles/tokens';
import { CalculatedMetricWithExplanation } from '@/types/metrics';

interface ClientMetricsCardsProps {
  metrics: CalculatedMetricWithExplanation[];
  loading: boolean;
  error: boolean;
  /** Opens the calculator explanation for a metric code (bmi, bmr, ...). */
  onInfo: (metric: string) => void;
}

const formatValue = (value: CalculatedMetricWithExplanation['value']) =>
  typeof value === 'number' ? String(Number(value.toFixed(1))) : value ?? '—';

/** Client calculated metrics (Figma "Метрики"): code, value and an info button per metric. */
export const ClientMetricsCards: React.FC<ClientMetricsCardsProps> = ({ metrics, loading, error, onInfo }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'clientProfile.metricsCards' });

  return (
    <Box data-i-doc={SectionIndices.EXPERT_CLIENT_METRICS_CARDS} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CalculateIcon sx={{ fontSize: 20, color: c.titleIcon }} />
        <Typography sx={{ fontSize: 16, lineHeight: '19px', fontWeight: 500, color: c.text }}>{t('title')}</Typography>
      </Box>

      {loading ? (
        <CircularProgress size={24} />
      ) : error ? (
        <Alert severity="warning">{t('loadError')}</Alert>
      ) : metrics.length === 0 ? (
        <Alert severity="info">{t('empty')}</Alert>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(auto-fill, minmax(175px, 1fr))' }, gap: 2 }}>
          {metrics.map((m) => (
            <Box
              key={m.name}
              sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 1.5, p: 2.5, bgcolor: c.background, boxShadow: c.shadow, borderRadius: '12px' }}
            >
              <Tooltip title={m.displayName ?? m.name}>
                <Typography sx={{ alignSelf: 'flex-start', fontSize: 14, lineHeight: '16px', textTransform: 'uppercase', color: c.label }}>
                  {m.name}
                </Typography>
              </Tooltip>
              <Typography sx={{ fontSize: 16, lineHeight: '19px', fontWeight: 600, color: c.text }}>{formatValue(m.value)}</Typography>
              <IconButton
                aria-label={t('info', { metric: m.name.toUpperCase() })}
                onClick={() => onInfo(m.name)}
                sx={{ position: 'absolute', top: 12, right: 12, width: 24, height: 24, p: 0.5, borderRadius: '4px', color: c.infoIcon }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
